import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, CalendarIcon, Download, ExternalLink } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface Submission {
  score_id: string;
  created_at: string;
  org_id: string;
  org_name: string;
  user_id: string;
  user_name: string | null;
  source: string;
  rubric_score: number;
  fsr_at_time: number | null;
}

interface OrgOption {
  org_id: string;
  org_name: string;
}

interface ScoreDetail {
  evaluation_markdown: string;
  dimension_scores_json: Record<string, number>;
  overall_score_1_100: number;
  best_prompt_extracted: string | null;
}

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedOrg, setSelectedOrg] = useState<string>('all');
  const [userSearch, setUserSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  // View details
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [scoreDetail, setScoreDetail] = useState<ScoreDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchOrgs = async () => {
    try {
      const { data, error } = await supabase.rpc('get_all_orgs_admin');
      if (error) throw error;
      setOrgs(data?.map(o => ({ org_id: o.org_id, org_name: o.org_name })) || []);
    } catch (error) {
      console.error('Error fetching orgs:', error);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_global_submissions_admin', {
        _org_id: selectedOrg === 'all' ? null : selectedOrg,
        _start_date: startDate.toISOString(),
        _end_date: endDate.toISOString(),
        _source: sourceFilter === 'all' ? null : sourceFilter,
        _limit: 200,
        _offset: 0,
      });
      
      if (error) throw error;
      
      // Filter by user search client-side
      let filtered = data || [];
      if (userSearch.trim()) {
        const search = userSearch.toLowerCase();
        filtered = filtered.filter(s => 
          s.user_name?.toLowerCase().includes(search) || 
          s.user_id.toLowerCase().includes(search)
        );
      }
      
      setSubmissions(filtered);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [selectedOrg, sourceFilter, startDate, endDate]);

  const handleSearch = () => {
    fetchSubmissions();
  };

  const viewSubmissionDetail = async (submission: Submission) => {
    setSelectedSubmission(submission);
    setLoadingDetail(true);
    
    try {
      // Fetch the full score details
      const { data, error } = await supabase
        .from('fluency_scores')
        .select('evaluation_markdown, dimension_scores_json, overall_score_1_100, best_prompt_extracted')
        .eq('id', submission.score_id)
        .single();
      
      if (error) throw error;
      setScoreDetail(data as ScoreDetail);
    } catch (error) {
      console.error('Error fetching score detail:', error);
      toast.error('Failed to load submission details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Organization', 'User', 'Source', 'Rubric Score', 'FSR at Time'];
    const rows = submissions.map(s => [
      format(new Date(s.created_at), 'yyyy-MM-dd HH:mm'),
      s.org_name,
      s.user_name || s.user_id,
      s.source,
      s.rubric_score,
      s.fsr_at_time || '',
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('CSV exported');
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'fluency_submission': return 'Prompt';
      case 'prompt_submission': return 'Legacy';
      default: return source;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Submissions</h1>
          <p className="text-muted-foreground">Explore scored submissions across all organizations</p>
        </div>
        <Button variant="outline" onClick={exportCSV} disabled={submissions.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">Organization</label>
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {orgs.map((org) => (
                <SelectItem key={org.org_id} value={org.org_id}>
                  {org.org_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">User</label>
          <div className="flex gap-2">
            <Input
              placeholder="Search by name..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-48"
            />
            <Button variant="outline" size="icon" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">Type</label>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="fluency_submission">Prompt</SelectItem>
              <SelectItem value="prompt_submission">Legacy</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">Start Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-36 justify-start text-left font-normal")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(startDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1">
          <label className="text-sm text-muted-foreground">End Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-36 justify-start text-left font-normal")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(endDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {submissions.length} submissions found
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Rubric Score</TableHead>
              <TableHead>FSR at Time</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No submissions found
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((sub) => (
                <TableRow key={sub.score_id}>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(sub.created_at), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell className="font-medium">{sub.org_name}</TableCell>
                  <TableCell>{sub.user_name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{getSourceLabel(sub.source)}</Badge>
                  </TableCell>
                  <TableCell className="font-semibold">{sub.rubric_score}</TableCell>
                  <TableCell>{sub.fsr_at_time || '-'}</TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => viewSubmissionDetail(sub)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : scoreDetail && selectedSubmission ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Organization:</span>{' '}
                  <span className="font-medium">{selectedSubmission.org_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">User:</span>{' '}
                  <span className="font-medium">{selectedSubmission.user_name || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>{' '}
                  <span className="font-medium">{format(new Date(selectedSubmission.created_at), 'MMM d, yyyy HH:mm')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Overall Score:</span>{' '}
                  <span className="font-semibold text-lg">{scoreDetail.overall_score_1_100}</span>
                </div>
              </div>

              {scoreDetail.best_prompt_extracted && (
                <div className="space-y-2">
                  <h3 className="font-medium">Best Prompt Extracted</h3>
                  <div className="bg-muted/50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                    {scoreDetail.best_prompt_extracted}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="font-medium">Dimension Scores</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(scoreDetail.dimension_scores_json || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between bg-muted/30 rounded px-2 py-1">
                      <span className="text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{value as number}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">Evaluation</h3>
                <div className="bg-muted/50 rounded-lg p-3 text-sm prose prose-sm max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: scoreDetail.evaluation_markdown.replace(/\n/g, '<br>') }} />
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
